import {
  Activity,
  ArrowRight,
  BrainCircuit,
  Braces,
  ChartNoAxesCombined,
  CheckCircle2,
  ChevronRight,
  CircleGauge,
  Compass,
  Cpu,
  DatabaseZap,
  GitBranch,
  Layers3,
  LockKeyhole,
  Mail,
  Network,
  Orbit,
  PanelTop,
  Radar,
  ShieldCheck,
  Sparkles,
  Terminal,
  Workflow,
  Zap,
} from "lucide-react";
import { useEffect, useRef } from "react";

const capabilities = [
  {
    icon: Network,
    eyebrow: "上下文图谱",
    title: "让每个答案都能回到工作的脉络",
    body: "将文档、决策、工单与系统轨迹连接成空间化知识层，智能体可以在其中定位和推理。",
  },
  {
    icon: BrainCircuit,
    eyebrow: "智能体运行空间",
    title: "给 AI 一个可以推理的场所",
    body: "统一任务记忆、工具调用与人工检查点，避免上下文散落在多个页面和对话中。",
  },
  {
    icon: CircleGauge,
    eyebrow: "运营驾驶舱",
    title: "在行动前看清置信度",
    body: "在 AI 工作流触达生产状态前，追踪来源、风险与就绪信号。",
  },
];

const stackItems = [
  ["01", "采集", "连接知识、代码、产品界面与实时运营信号。"],
  ["02", "建模", "围绕团队、实体、任务和依赖关系生成可导航空间。"],
  ["03", "协同", "让 AI 行动经过策略、责任人和审批路径。"],
  ["04", "观测", "衡量漂移、覆盖率、鲜活度以及自动化带来的真实业务影响。"],
];

const proofPoints = [
  "决策记忆",
  "工具感知智能体",
  "来源链路",
  "人工确认",
  "系统地图",
  "实时上下文",
  "AI 工作空间",
  "运营遥测",
];

const sectors = [
  {
    icon: DatabaseZap,
    label: "知识系统",
    text: "统一知识来源，而不是让团队再维护一个静态知识库。",
  },
  {
    icon: PanelTop,
    label: "产品运营",
    text: "看清影响路线图的决策、指标与依赖关系。",
  },
  {
    icon: LockKeyhole,
    label: "可治理自动化",
    text: "在智能体行动前加入置信门槛和责任链路。",
  },
  {
    icon: ChartNoAxesCombined,
    label: "管理层感知",
    text: "把碎片化信号转化为组织投入方向的动态视图。",
  },
];

const faqs = [
  {
    question: "SeekSpace AI 是什么？",
    answer:
      "SeekSpace AI 是面向 AI 原生团队的空间智能平台，用来把企业知识、决策、工具和实时信号组织成可导航的 AI 工作空间。",
  },
  {
    question: "SeekSpace AI 适合哪些团队？",
    answer:
      "它适合正在建设智能体工作流、知识系统、产品运营体系和可治理自动化能力的团队，尤其适合需要跨工具保留上下文的组织。",
  },
  {
    question: "SeekSpace AI 解决什么问题？",
    answer:
      "它解决 AI 工作流缺少上下文地图的问题，让智能体在执行任务前理解来源、责任、依赖、风险和人工确认路径。",
  },
  {
    question: "SeekSpace AI 是否需要后端或数据库才能部署官网？",
    answer:
      "当前官网是静态 Vite 页面，可以直接部署到 Cloudflare Pages；产品试点和表单能力可以后续再接入 Cloudflare Pages Functions 或其他服务。",
  },
];

const toolchainCards = [
  {
    icon: Terminal,
    command: "seekspace ingest",
    title: "统一上下文入口",
    output: ["sources indexed", "lineage attached", "policy scope ready"],
  },
  {
    icon: GitBranch,
    command: "seekspace model",
    title: "生成工作空间图谱",
    output: ["entities resolved", "owners mapped", "dependency graph live"],
  },
  {
    icon: ShieldCheck,
    command: "seekspace observe",
    title: "持续观测与治理",
    output: ["risk gates active", "drift monitored", "handoff trail signed"],
  },
];

const employeeCapabilities = [
  {
    icon: Workflow,
    eyebrow: "配置驱动",
    title: "用 workflow 定义每位硅基员工",
    body: "通过员工配置描述使命、可服务会话、owner、回复策略、业务时段和模型边界，让员工能力可以被复制、审计和迭代。",
  },
  {
    icon: BrainCircuit,
    eyebrow: "画像蒸馏",
    title: "从材料中蒸馏员工画像与技能",
    body: "把文档、访谈、记录和经验材料转化为可复用的员工 profile 与 SKILL.md，减少从零提示和人工调教成本。",
  },
  {
    icon: Network,
    eyebrow: "IM 运行时",
    title: "在飞书会话中接收、理解和回复",
    body: "通过 Feishu channel 处理消息、卡片回调和群聊身份识别，把 AI 员工嵌入团队已经工作的地方。",
  },
];

const employeeRuntimeSteps = [
  ["01", "路由", "识别会话、owner、@ 提及和员工 workflow，决定消息是否进入处理流。"],
  ["02", "装载", "读取员工画像、长期记忆、历史对话、附件理解结果和待跟进事项。"],
  [
    "03",
    "行动",
    "由 LangGraph 主流程和 ReplyAgent 工具生成回复、创建跟进、检索附件或触发 owner 指令。",
  ],
  ["04", "沉淀", "持久化入站/出站事件，抽取记忆，并在定时任务中完成进化、简报和持续跟进。"],
];

const employeeUseCases = [
  {
    icon: Compass,
    label: "CEO / 策略员工",
    text: "帮助缩小范围、识别取舍、排序 MVP 工作，并在讨论中保持清晰边界。",
  },
  {
    icon: DatabaseZap,
    label: "知识型员工",
    text: "吸收本地 seed 材料与历史对话，在团队问答中带回可追溯的上下文。",
  },
  {
    icon: CircleGauge,
    label: "跟进型员工",
    text: "持续同步线程里的事项状态，在业务时段内提醒 owner 推进下一步。",
  },
  {
    icon: ShieldCheck,
    label: "可治理员工",
    text: "把员工使命、会话范围、权限和回调行为放进配置与运行记录中管理。",
  },
];

const employeeProofPoints = [
  "LangGraph StateGraph",
  "Feishu Channel",
  "Profile Distillation",
  "Follow-up Tasks",
  "Owner Briefing",
  "Memory Extraction",
  "Attachment Recall",
  "Workflow Routing",
];

const employeeFaqs = [
  {
    question: "硅基员工是什么？",
    answer:
      "硅基员工是 SeekSpace AI 的子产品，用配置、材料蒸馏和消息运行时，为团队创建可以在 IM 中工作的 AI 员工。",
  },
  {
    question: "它和普通聊天机器人有什么不同？",
    answer:
      "硅基员工不只回复单轮问题。它有员工画像、长期记忆、workflow 路由、owner 指令、跟进任务和定时进化机制，更接近一个持续工作的团队角色。",
  },
  {
    question: "当前优先支持哪些工作入口？",
    answer:
      "当前产品以飞书会话和卡片回调为主要入口，并通过 channel 适配层保留扩展到更多协作工具的边界。",
  },
  {
    question: "是否必须每位员工都重新开发？",
    answer:
      "不需要。员工使命、路由范围、模型、业务时段、seed 材料和跟进策略都可以通过配置与蒸馏流程复用。",
  },
];

const homeMetadata = {
  title: "SeekSpace AI | 面向 AI 原生团队的空间智能",
  description:
    "SeekSpace AI 是面向 AI 原生团队的空间智能平台，将企业知识、决策、工具和实时信号组织成可导航的 AI 工作空间。",
  path: "/",
};

const employeeMetadata = {
  title: "硅基员工 si-employee | SeekSpace AI",
  description:
    "硅基员工是 SeekSpace AI 的子产品，用配置驱动 workflow、画像蒸馏、飞书运行时、记忆和跟进任务，为团队创建可持续工作的 AI 员工。",
  path: "/si-employee",
};

function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const isEmployeePage = path === "/si-employee" || path === "/products/si-employee";

  useReveal();
  usePageMetadata(isEmployeePage ? employeeMetadata : homeMetadata);

  if (isEmployeePage) {
    return <SiEmployeeLandingPage />;
  }

  return (
    <main className="site-shell">
      <AnnouncementBar />
      <Header />
      <Hero />
      <LogoRail />
      <ToolchainBand />
      <Capabilities />
      <SystemMap />
      <UseCases />
      <FaqSection />
      <FinalCta />
    </main>
  );
}

function SiEmployeeLandingPage() {
  return (
    <main className="site-shell product-shell">
      <AnnouncementBar page="employee" />
      <Header page="employee" />
      <SiEmployeeHero />
      <EmployeeSignalRail />
      <SiEmployeeCapabilitySection />
      <EmployeeRuntimeSection />
      <EmployeeWorkflowSection />
      <EmployeeUseCases />
      <EmployeeFaqSection />
      <EmployeeFinalCta />
    </main>
  );
}

function AnnouncementBar({ page = "home" }: { page?: "home" | "employee" }) {
  const isEmployeePage = page === "employee";

  return (
    <div className="announcement-strip">
      <div className="announcement-inner">
        <span className="status-dot" aria-hidden="true" />
        <span>{isEmployeePage ? "si-employee runtime" : "seekspace graph/runtime"}</span>
        <strong>{isEmployeePage ? "feishu ready" : "running"}</strong>
        <a
          className="announcement-action"
          href={`mailto:hello@seekspace.ai?subject=${encodeURIComponent(
            isEmployeePage ? "咨询硅基员工 si-employee" : "申请 SeekSpace AI 试点",
          )}`}
        >
          {isEmployeePage ? "预约演示" : "申请试点"}
          <ArrowRight size={14} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}

function Header({ page = "home" }: { page?: "home" | "employee" }) {
  const isEmployeePage = page === "employee";
  const navLinks = isEmployeePage
    ? [
        { href: "#features", label: "功能", current: false },
        { href: "#runtime", label: "运行时", current: false },
        { href: "#workflow", label: "工作流", current: false },
        { href: "/si-employee", label: "硅基员工", current: true },
      ]
    : [
        { href: "#capabilities", label: "能力", current: false },
        { href: "#system", label: "系统", current: false },
        { href: "#use-cases", label: "场景", current: false },
        { href: "/si-employee", label: "硅基员工", current: false },
      ];

  return (
    <header className="site-header">
      <a className="brand" href="/" aria-label="SeekSpace AI 首页">
        <span className="brand-mark" aria-hidden="true">
          <span />
        </span>
        <span>SeekSpace AI</span>
      </a>
      <nav className="nav-links" aria-label="主导航">
        {navLinks.map((link) => (
          <a href={link.href} aria-current={link.current ? "page" : undefined} key={link.href}>
            {link.label}
          </a>
        ))}
      </nav>
      <a
        className="header-action"
        href={`mailto:hello@seekspace.ai?subject=${encodeURIComponent(
          isEmployeePage ? "咨询硅基员工 si-employee" : "联系 SeekSpace AI",
        )}`}
      >
        <Mail size={16} aria-hidden="true" />
        {isEmployeePage ? "预约演示" : "联系我们"}
      </a>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" aria-labelledby="hero-title">
      <SignalCanvas />
      <div className="hero-glow hero-glow-a" />
      <div className="hero-glow hero-glow-b" />
      <div className="section-inner hero-inner">
        <div className="hero-copy">
          <div className="eyebrow reveal">
            <Sparkles size={16} aria-hidden="true" />
            面向 AI 原生团队的空间智能
          </div>
          <h1 id="hero-title" className="reveal">
            <span className="hero-line">
              为每个 <span className="nowrap">AI 系统</span>
            </span>
            <span className="hero-line">建立工作的地图。</span>
          </h1>
          <p className="hero-lede reveal">
            SeekSpace AI 将企业知识、决策、工具与实时信号转化为可导航的运营空间，
            让智能体和团队在复杂协作中保持方向感。
          </p>
          <div className="hero-actions reveal">
            <a className="primary-button" href="mailto:hello@seekspace.ai">
              申请早期体验
              <ArrowRight size={18} aria-hidden="true" />
            </a>
            <a className="secondary-button" href="#system">
              查看系统设计
              <ChevronRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="hero-visual reveal" aria-label="动态 AI 工作空间地图">
          <div className="orbital-stage">
            <div className="orbit-ring orbit-ring-one" />
            <div className="orbit-ring orbit-ring-two" />
            <div className="orbit-ring orbit-ring-three" />
            <div className="core-node">
              <span>AI</span>
              <strong>SeekSpace</strong>
            </div>
            <div className="orbit-node node-a">
              <Compass size={18} aria-hidden="true" />
              产品
            </div>
            <div className="orbit-node node-b">
              <Layers3 size={18} aria-hidden="true" />
              上下文
            </div>
            <div className="orbit-node node-c">
              <Radar size={18} aria-hidden="true" />
              信号
            </div>
            <div className="orbit-node node-d">
              <Zap size={18} aria-hidden="true" />
              行动
            </div>
          </div>
          <RuntimeConsole />
        </div>
      </div>
    </section>
  );
}

function RuntimeConsole() {
  return (
    <aside className="runtime-console" aria-label="SeekSpace 运行时控制台">
      <div className="console-top">
        <span className="window-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span>seekspace.runtime</span>
        <strong>live</strong>
      </div>
      <div className="console-lines">
        <p>
          <span className="prompt">$</span> seekspace map --workspace ops
        </p>
        <p>
          <CheckCircle2 size={14} aria-hidden="true" />
          sources linked <em>128</em>
        </p>
        <p>
          <Workflow size={14} aria-hidden="true" />
          agent handoff policy <em>active</em>
        </p>
        <p>
          <Braces size={14} aria-hidden="true" />
          context graph <em>ready</em>
        </p>
      </div>
      <div className="console-metrics">
        <span>
          <Cpu size={14} aria-hidden="true" />
          89ms
        </span>
        <span>
          <Activity size={14} aria-hidden="true" />
          94%
        </span>
        <span>
          <ShieldCheck size={14} aria-hidden="true" />
          gated
        </span>
      </div>
    </aside>
  );
}

function SignalCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointer = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }

    const particles = Array.from({ length: 64 }, (_, index) => ({
      x: Math.random(),
      y: Math.random(),
      r: 0.7 + Math.random() * 1.9,
      speed: 0.08 + Math.random() * 0.22,
      phase: index * 0.21,
      hue: index % 3,
    }));

    let frame = 0;
    let animation = 0;
    let width = 0;
    let height = 0;
    let pixelRatio = 1;

    const resize = () => {
      pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = Math.max(1, Math.floor(width * pixelRatio));
      canvas.height = Math.max(1, Math.floor(height * pixelRatio));
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    const draw = () => {
      frame += 0.008;
      context.clearRect(0, 0, width, height);

      const gradient = context.createRadialGradient(
        width * (0.32 + pointer.current.x * 0.08),
        height * (0.2 + pointer.current.y * 0.08),
        0,
        width * 0.5,
        height * 0.28,
        Math.max(width, height) * 0.65,
      );
      gradient.addColorStop(0, "rgba(232, 255, 102, 0.15)");
      gradient.addColorStop(0.4, "rgba(70, 242, 214, 0.08)");
      gradient.addColorStop(1, "rgba(5, 6, 7, 0)");
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);

      const projected = particles.map((particle) => {
        const driftX = Math.sin(frame * particle.speed + particle.phase) * 0.035;
        const driftY = Math.cos(frame * (particle.speed + 0.09) + particle.phase) * 0.035;
        const x = (particle.x + driftX + pointer.current.x * 0.018) * width;
        const y = (particle.y + driftY + pointer.current.y * 0.018) * height;
        return { ...particle, px: x, py: y };
      });

      for (let i = 0; i < projected.length; i += 1) {
        for (let j = i + 1; j < projected.length; j += 1) {
          const a = projected[i];
          const b = projected[j];
          const distance = Math.hypot(a.px - b.px, a.py - b.py);
          if (distance < 150) {
            context.strokeStyle = `rgba(232, 255, 102, ${0.12 - distance / 1400})`;
            context.lineWidth = 0.7;
            context.beginPath();
            context.moveTo(a.px, a.py);
            context.lineTo(b.px, b.py);
            context.stroke();
          }
        }
      }

      for (const particle of projected) {
        const color =
          particle.hue === 0
            ? "232, 255, 102"
            : particle.hue === 1
              ? "70, 242, 214"
              : "245, 168, 75";
        context.fillStyle = `rgba(${color}, 0.86)`;
        context.beginPath();
        context.arc(particle.px, particle.py, particle.r, 0, Math.PI * 2);
        context.fill();
      }

      animation = window.requestAnimationFrame(draw);
    };

    const handlePointer = (event: PointerEvent) => {
      pointer.current = {
        x: event.clientX / Math.max(window.innerWidth, 1),
        y: event.clientY / Math.max(window.innerHeight, 1),
      };
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", handlePointer);

    return () => {
      window.cancelAnimationFrame(animation);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", handlePointer);
    };
  }, []);

  return <canvas ref={canvasRef} className="signal-canvas" aria-hidden="true" />;
}

function LogoRail() {
  return (
    <section className="signal-rail" aria-label="SeekSpace AI 关注领域">
      <div className="rail-track">
        {[...proofPoints, ...proofPoints].map((item, index) => (
          <span key={`${item}-${index}`}>{item}</span>
        ))}
      </div>
    </section>
  );
}

function SiEmployeeHero() {
  return (
    <section className="hero product-hero" aria-labelledby="employee-hero-title">
      <SignalCanvas />
      <div className="hero-glow hero-glow-a" />
      <div className="hero-glow hero-glow-b" />
      <div className="section-inner hero-inner product-hero-inner">
        <div className="hero-copy">
          <div className="eyebrow reveal">
            <Sparkles size={16} aria-hidden="true" />
            SeekSpace AI 子产品 · si-employee
          </div>
          <h1 id="employee-hero-title" className="reveal">
            <span className="hero-line">把团队角色</span>
            <span className="hero-line">
              部署成<span className="nowrap">硅基员工。</span>
            </span>
          </h1>
          <p className="hero-lede reveal">
            硅基员工用配置驱动的 workflow、画像蒸馏、飞书运行时、长期记忆和持续跟进，
            为团队创建可以在真实协作场景中工作的 AI 员工。
          </p>
          <div className="hero-actions reveal">
            <a
              className="primary-button"
              href={`mailto:hello@seekspace.ai?subject=${encodeURIComponent(
                "咨询硅基员工 si-employee",
              )}`}
            >
              预约产品演示
              <ArrowRight size={18} aria-hidden="true" />
            </a>
            <a className="secondary-button" href="#runtime">
              查看运行方式
              <ChevronRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="employee-visual reveal" aria-label="硅基员工工作流界面">
          <div className="employee-console">
            <div className="console-top">
              <span className="window-dots" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <span>si-employee.ceo</span>
              <strong>active</strong>
            </div>
            <div className="employee-card employee-card-primary">
              <span className="employee-avatar">SI</span>
              <div>
                <p className="employee-card-label">mission</p>
                <h2>帮助团队做出更清晰的产品、策略和优先级判断。</h2>
              </div>
            </div>
            <div className="employee-thread">
              <p>
                <span>@硅基 CEO</span> 这个版本应该先做企业群试点还是公开发布？
              </p>
              <p>
                <strong>建议先做企业群试点。</strong>
                当前依赖集中在配置、owner 路径和跟进闭环，公开发布前需要收集真实协作反馈。
              </p>
            </div>
            <div className="employee-metrics">
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                workflow matched
              </span>
              <span>
                <BrainCircuit size={14} aria-hidden="true" />
                memory loaded
              </span>
              <span>
                <Workflow size={14} aria-hidden="true" />
                follow-up queued
              </span>
            </div>
          </div>
          <div className="employee-side-panel" aria-hidden="true">
            <span>profile</span>
            <strong>distilled</strong>
            <i />
          </div>
        </div>
      </div>
    </section>
  );
}

function EmployeeSignalRail() {
  return (
    <section className="signal-rail" aria-label="硅基员工产品能力">
      <div className="rail-track">
        {[...employeeProofPoints, ...employeeProofPoints].map((item, index) => (
          <span key={`${item}-${index}`}>{item}</span>
        ))}
      </div>
    </section>
  );
}

function SiEmployeeCapabilitySection() {
  return (
    <section id="features" className="section-block">
      <div className="section-inner">
        <div className="section-heading reveal">
          <p className="section-kicker">产品功能</p>
          <h2>把员工能力从提示词变成可运行系统。</h2>
          <p>
            si-employee 将员工定义、会话入口、上下文装载、回复行动和长期沉淀放进一条可观测的工作流。
          </p>
        </div>
        <div className="capability-grid">
          {employeeCapabilities.map((capability) => {
            const Icon = capability.icon;
            return (
              <article className="capability-card reveal" key={capability.title}>
                <div className="card-icon">
                  <Icon size={22} aria-hidden="true" />
                </div>
                <span>{capability.eyebrow}</span>
                <h3>{capability.title}</h3>
                <p>{capability.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function EmployeeRuntimeSection() {
  return (
    <section id="runtime" className="section-block system-section employee-runtime-section">
      <div className="section-inner system-layout">
        <div className="system-copy reveal">
          <p className="section-kicker">Runtime loop</p>
          <h2>从一条群消息，到一个可追踪的员工动作。</h2>
          <p>
            硅基员工以 LangGraph
            承载主消息流，围绕路由、上下文、工具调用、持久化和记忆抽取形成闭环。
          </p>
          <a
            href={`mailto:hello@seekspace.ai?subject=${encodeURIComponent("试点 si-employee")}`}
            className="text-link"
          >
            讨论试点部署
            <ArrowRight size={17} aria-hidden="true" />
          </a>
        </div>
        <div className="stack-timeline" aria-label="硅基员工运行时流程">
          {employeeRuntimeSteps.map(([number, title, text]) => (
            <article className="timeline-item reveal" key={title}>
              <span>{number}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function EmployeeWorkflowSection() {
  return (
    <section id="workflow" className="toolchain-band employee-workflow-band">
      <div className="section-inner toolchain-inner">
        <div className="toolchain-intro reveal">
          <p className="section-kicker">Employee workflow</p>
          <h2>配置、蒸馏、运行、进化，形成员工生命周期。</h2>
        </div>
        <div className="toolchain-grid">
          <article className="toolchain-card reveal">
            <div className="toolchain-command">
              <Braces size={18} aria-hidden="true" />
              <code>config/employee/*.yaml</code>
            </div>
            <h3>定义员工边界</h3>
            <div className="toolchain-output">
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                mission / owner / channel
              </span>
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                business hours
              </span>
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                allowed conversations
              </span>
            </div>
          </article>
          <article className="toolchain-card reveal">
            <div className="toolchain-command">
              <Terminal size={18} aria-hidden="true" />
              <code>$ si-employees distill</code>
            </div>
            <h3>生成员工画像</h3>
            <div className="toolchain-output">
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                seed materials indexed
              </span>
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                profile generated
              </span>
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                SKILL.md ready
              </span>
            </div>
          </article>
          <article className="toolchain-card reveal">
            <div className="toolchain-command">
              <Activity size={18} aria-hidden="true" />
              <code>$ si-employees serve</code>
            </div>
            <h3>进入团队会话</h3>
            <div className="toolchain-output">
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                Feishu callbacks
              </span>
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                follow-up scheduler
              </span>
              <span>
                <CheckCircle2 size={14} aria-hidden="true" />
                owner briefing
              </span>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function EmployeeUseCases() {
  return (
    <section id="employee-cases" className="section-block">
      <div className="section-inner">
        <div className="section-heading reveal">
          <p className="section-kicker">适用角色</p>
          <h2>先从高上下文、高重复协作的岗位开始。</h2>
        </div>
        <div className="use-case-grid">
          {employeeUseCases.map((sector) => {
            const Icon = sector.icon;
            return (
              <article className="use-case reveal" key={sector.label}>
                <Icon size={21} aria-hidden="true" />
                <h3>{sector.label}</h3>
                <p>{sector.text}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function EmployeeFaqSection() {
  return (
    <section id="faq" className="section-block faq-section" aria-labelledby="employee-faq-title">
      <div className="section-inner faq-layout">
        <div className="section-heading reveal">
          <p className="section-kicker">常见问题</p>
          <h2 id="employee-faq-title">快速理解 si-employee。</h2>
          <p>围绕产品定位、团队入口、员工复用和持续工作方式回答常见问题。</p>
        </div>
        <div className="faq-list">
          {employeeFaqs.map((faq) => (
            <article className="faq-item reveal" key={faq.question}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function EmployeeFinalCta() {
  return (
    <section className="final-cta employee-final-cta">
      <div className="section-inner final-inner reveal">
        <div>
          <p className="section-kicker">si-employee</p>
          <h2>先复制一个可信员工，再扩展一组 AI 团队。</h2>
        </div>
        <a
          href={`mailto:hello@seekspace.ai?subject=${encodeURIComponent("预约硅基员工产品演示")}`}
          className="primary-button"
        >
          预约演示
          <Orbit size={18} aria-hidden="true" />
        </a>
      </div>
      <footer className="site-footer">
        <span>SeekSpace AI · si-employee</span>
        <a href="mailto:hello@seekspace.ai">hello@seekspace.ai</a>
        <a href="/">返回 SeekSpace AI 首页</a>
      </footer>
    </section>
  );
}

function ToolchainBand() {
  return (
    <section className="toolchain-band" aria-label="SeekSpace AI 工具链流程">
      <div className="section-inner toolchain-inner">
        <div className="toolchain-intro reveal">
          <p className="section-kicker">Runtime workflow</p>
          <h2>把 AI 工作流像工具链一样交付。</h2>
        </div>
        <div className="toolchain-grid">
          {toolchainCards.map((card) => {
            const Icon = card.icon;
            return (
              <article className="toolchain-card reveal" key={card.command}>
                <div className="toolchain-command">
                  <Icon size={18} aria-hidden="true" />
                  <code>$ {card.command}</code>
                </div>
                <h3>{card.title}</h3>
                <div className="toolchain-output">
                  {card.output.map((line) => (
                    <span key={line}>
                      <CheckCircle2 size={14} aria-hidden="true" />
                      {line}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Capabilities() {
  return (
    <section id="capabilities" className="section-block">
      <div className="section-inner">
        <div className="section-heading reveal">
          <p className="section-kicker">核心能力</p>
          <h2>为依赖动态上下文的 AI 工作而设计。</h2>
          <p>
            SeekSpace 将公司上下文带入空间化层级，让人、智能体和系统在工作变化时 依然保持清晰方向。
          </p>
        </div>
        <div className="capability-grid">
          {capabilities.map((capability) => {
            const Icon = capability.icon;
            return (
              <article className="capability-card reveal" key={capability.title}>
                <div className="card-icon">
                  <Icon size={22} aria-hidden="true" />
                </div>
                <span>{capability.eyebrow}</span>
                <h3>{capability.title}</h3>
                <p>{capability.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SystemMap() {
  return (
    <section id="system" className="section-block system-section">
      <div className="section-inner system-layout">
        <div className="system-copy reveal">
          <p className="section-kicker">系统架构</p>
          <h2>像运营图谱一样流动的工作空间。</h2>
          <p>
            不再让团队在另一堆页面里搜索信息。SeekSpace 构建工作、知识与责任如何 连接的可导航模型。
          </p>
          <a href="mailto:hello@seekspace.ai" className="text-link">
            讨论试点
            <ArrowRight size={17} aria-hidden="true" />
          </a>
        </div>
        <div className="stack-timeline" aria-label="SeekSpace AI 系统流程">
          {stackItems.map(([number, title, text]) => (
            <article className="timeline-item reveal" key={title}>
              <span>{number}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  return (
    <section id="use-cases" className="section-block">
      <div className="section-inner">
        <div className="section-heading reveal">
          <p className="section-kicker">使用场景</p>
          <h2>为走出聊天框的团队而建。</h2>
        </div>
        <div className="use-case-grid">
          {sectors.map((sector) => {
            const Icon = sector.icon;
            return (
              <article className="use-case reveal" key={sector.label}>
                <Icon size={21} aria-hidden="true" />
                <h3>{sector.label}</h3>
                <p>{sector.text}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section id="faq" className="section-block faq-section" aria-labelledby="faq-title">
      <div className="section-inner faq-layout">
        <div className="section-heading reveal">
          <p className="section-kicker">常见问题</p>
          <h2 id="faq-title">快速理解 SeekSpace AI。</h2>
          <p>用简短问题回答产品定位、适用团队、核心问题和部署方式。</p>
        </div>
        <div className="faq-list">
          {faqs.map((faq) => (
            <article className="faq-item reveal" key={faq.question}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="final-cta">
      <div className="section-inner final-inner reveal">
        <div>
          <p className="section-kicker">seekspace.ai</p>
          <h2>先建立地图，再扩展智能体。</h2>
        </div>
        <a href="mailto:hello@seekspace.ai" className="primary-button">
          开始沟通
          <Orbit size={18} aria-hidden="true" />
        </a>
      </div>
      <footer className="site-footer">
        <span>SeekSpace AI</span>
        <a href="mailto:hello@seekspace.ai">hello@seekspace.ai</a>
        <span>Cloudflare Pages 就绪</span>
      </footer>
    </section>
  );
}

function useReveal() {
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.18 },
    );

    for (const element of elements) {
      observer.observe(element);
    }

    const initial = window.setTimeout(() => {
      for (const element of elements.slice(0, 5)) {
        element.classList.add("in-view");
      }
    }, 80);

    return () => {
      window.clearTimeout(initial);
      observer.disconnect();
    };
  }, []);
}

function usePageMetadata({
  title,
  description,
  path,
}: {
  title: string;
  description: string;
  path: string;
}) {
  useEffect(() => {
    const absoluteUrl = `https://seekspace.ai${path === "/" ? "/" : path}`;

    document.title = title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", absoluteUrl);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setLink("canonical", absoluteUrl);
  }, [description, path, title]);
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }

  meta.content = content;
}

function setLink(rel: string, href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }

  link.href = href;
}

export default App;
